/**
 * Shared React Query retry helpers.
 * Keep booking/payment mutations on retry: false unless the API is idempotent.
 */

/** Exponential backoff: 1s, 2s, 4s... capped at 30s */
export const retryDelay = (attemptIndex) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

/** Never retry these client / auth / validation responses */
export const NO_RETRY_STATUSES = [400, 401, 403, 404, 422, 429];

/** Only retry these transient server errors */
export const RETRY_STATUSES = [500, 502, 503, 504];

/** Max retry attempts after the initial failure (3 retries total) */
export const MAX_RETRIES = 3;

export const isNetworkError = (error) => {
  if (!error) return false;
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return !error.response;
};

export const isRetryableStatus = (status) =>
  status != null && RETRY_STATUSES.includes(status);

export const shouldNotRetryByStatus = (status) =>
  status != null && NO_RETRY_STATUSES.includes(status);

/**
 * Queries: retry network blips and 500/502/503/504 only (max 3 retries).
 * Never retry 400, 401, 403, 404, 422, 429.
 */
export const shouldRetryQuery = (failureCount, error) => {
  if (failureCount >= MAX_RETRIES) return false;
  if (isNetworkError(error)) return true;

  const status = error?.response?.status;
  if (shouldNotRetryByStatus(status)) return false;
  return isRetryableStatus(status);
};

/**
 * Safe mutations only (OTP/SMS, etc.). Never use for booking create / payment.
 * Same status rules and max 3 retries.
 */
export const shouldRetrySafeMutation = (failureCount, error) => {
  if (failureCount >= MAX_RETRIES) return false;
  if (isNetworkError(error)) return true;

  const status = error?.response?.status;
  if (shouldNotRetryByStatus(status)) return false;
  return isRetryableStatus(status);
};

/** Dangerous writes: no automatic retry (duplicate booking/payment risk). */
export const NO_RETRY = false;
